import React, { useState, useEffect } from 'react'
import { summaryApi } from '../../common'
import { useTheme } from '../../components/Theme/ThemeProvider'
import {
  Mail,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Send,
  Users,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { toast } from 'react-toastify'
import { authFetch } from '../../helpers/authFetch';

function NewsletterAdmin() {
  const { colors } = useTheme()
  const [subscribers, setSubscribers] = useState([])
  const [newsletterContent, setNewsletterContent] = useState({
    subject: '',
    content: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [filter, setFilter] = useState('all')
  const [message, setMessage] = useState('')

  useEffect(() => {
    fetchSubscribers()
  }, [filter])

  const fetchSubscribers = async () => {
    try {
      setIsFetching(true)
      const response = await authFetch(summaryApi.getNewsletterSubcription.url, {
        method: summaryApi.getNewsletterSubcription.method,
        
      })
      
      if (!response.ok) throw new Error('Failed to fetch subscribers')
      
      const data = await response.json()
      let filteredSubscribers = Array.isArray(data) ? data : []

      if (filter === 'active') {
        filteredSubscribers = filteredSubscribers.filter(sub => sub.isActive)
      } else if (filter === 'inactive') {
        filteredSubscribers = filteredSubscribers.filter(sub => !sub.isActive)
      }

      setSubscribers(filteredSubscribers)
    } catch (error) {
      toast.error('Failed to fetch subscribers')
      setSubscribers([])
    } finally {
      setIsFetching(false)
    }
  }

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Delete ${email}?`)) return

    try {
      await authFetch(summaryApi.deleteSubscription.url.replace(':email', email), {
        method: summaryApi.deleteSubscription.method,
       
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      toast.success(`Subscriber deleted`)
      fetchSubscribers()
    } catch (error) {
      toast.error('Failed to delete subscriber')
    }
  }

  const toggleSubscriberStatus = async (email, currentStatus) => {
    try {
      await authFetch(summaryApi.updateSubscriberStatus.url.replace(':email', email), {
        method: summaryApi.updateSubscriberStatus.method,
        
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, isActive: !currentStatus })
      })
      toast.success(`Subscriber ${!currentStatus ? 'activated' : 'deactivated'}`)
      fetchSubscribers()
    } catch (error) {
      toast.error('Failed to update subscriber')
    }
  }

  const sendNewsletterHandler = async (event) => {
    event.preventDefault()
    
    if (!newsletterContent.subject.trim() || !newsletterContent.content.trim()) {
      toast.error('Subject and content are required')
      return
    }

    setIsLoading(true)
    try {
      const response = await authFetch(
        summaryApi.sendNewsletterSubcription.url,
        {
          method: summaryApi.sendNewsletterSubcription.method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newsletterContent),
          
        }
      )

      const data = await response.json()
      if (!response.ok) throw new Error(data.message || 'Failed to send newsletter')

      toast.success(`Newsletter sent to ${data.recipientCount} subscribers`)
      setNewsletterContent({ subject: '', content: '' })
    } catch (error) {
      toast.error(error.message || 'Failed to send newsletter')
    } finally {
      setIsLoading(false)
    }
  }

  const stats = {
    total: subscribers.length,
    active: subscribers.filter(s => s.isActive).length,
    inactive: subscribers.filter(s => !s.isActive).length
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
          Newsletter Management
        </h1>
        <p style={{ color: colors.textSecondary }} className="text-lg">
          Send campaigns and manage subscribers
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Subscribers', value: stats.total, icon: Users, color: colors.primary },
          { label: 'Active', value: stats.active, icon: CheckCircle, color: '#10B981' },
          { label: 'Inactive', value: stats.inactive, icon: AlertCircle, color: '#EF4444' }
        ].map((stat, idx) => {
          const Icon = stat.icon
          return (
            <div
              key={idx}
              className="rounded-xl border p-6 transition-all hover:shadow-lg hover:scale-105 animate-slideUp"
              style={{
                backgroundColor: colors.bgSecondary,
                borderColor: colors.border,
                animationDelay: `${idx * 50}ms`
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p style={{ color: colors.textSecondary }} className="text-sm font-semibold mb-2">
                    {stat.label}
                  </p>
                  <p className="text-3xl font-bold" style={{ color: stat.color }}>
                    {stat.value}
                  </p>
                </div>
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <Icon className="w-8 h-8" style={{ color: stat.color }} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Compose Newsletter */}
        <div className="lg:col-span-2 animate-slideUp" style={{ animationDelay: '100ms' }}>
          <div
            className="rounded-xl border p-6"
            style={{
              backgroundColor: colors.bgSecondary,
              borderColor: colors.border
            }}
          >
            <h2 className="text-2xl font-bold mb-6" style={{ color: colors.text }}>
              Compose Newsletter
            </h2>

            <form onSubmit={sendNewsletterHandler} className="space-y-4">
              <div>
                <label style={{ color: colors.text }} className="block text-sm font-semibold mb-3">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={newsletterContent.subject}
                  onChange={(e) => setNewsletterContent({
                    ...newsletterContent,
                    subject: e.target.value
                  })}
                  placeholder="Enter newsletter subject..."
                  className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`
                  }}
                  required
                />
              </div>

              <div>
                <label style={{ color: colors.text }} className="block text-sm font-semibold mb-3">
                  Newsletter Content
                </label>
                <textarea
                  value={newsletterContent.content}
                  onChange={(e) => setNewsletterContent({
                    ...newsletterContent,
                    content: e.target.value
                  })}
                  placeholder="Write your newsletter content here..."
                  className="w-full px-4 py-3 rounded-lg outline-none focus:ring-2 transition-all resize-none"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    border: `2px solid ${colors.border}`,
                    minHeight: '300px'
                  }}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 text-white font-semibold rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg flex items-center justify-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                }}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Newsletter
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Subscribers List */}
        <div className="lg:col-span-1 animate-slideUp" style={{ animationDelay: '150ms' }}>
          <div
            className="rounded-xl border p-6 h-full flex flex-col"
            style={{
              backgroundColor: colors.bgSecondary,
              borderColor: colors.border
            }}
          >
            <h2 className="text-2xl font-bold mb-4" style={{ color: colors.text }}>
              Subscribers
            </h2>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-lg font-medium outline-none mb-4 transition-all"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                border: `2px solid ${colors.border}`
              }}
            >
              <option value="all">All Subscribers</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>

            <div className="flex-1 overflow-y-auto space-y-2">
              {isFetching ? (
                <div className="flex items-center justify-center h-40">
                  <div 
                    className="w-6 h-6 border-2 border-transparent rounded-full animate-spin"
                    style={{ borderTopColor: colors.primary }}
                  />
                </div>
              ) : subscribers.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" style={{ color: colors.textSecondary }} />
                  <p style={{ color: colors.textSecondary }} className="text-sm">
                    No subscribers
                  </p>
                </div>
              ) : (
                subscribers.map((subscriber, idx) => (
                  <div
                    key={subscriber._id}
                    className="p-3 rounded-lg border transition-all hover:shadow-md animate-slideUp"
                    style={{
                      backgroundColor: colors.bg,
                      borderColor: colors.border,
                      animationDelay: `${idx * 30}ms`
                    }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-xs font-medium truncate" style={{ color: colors.text }}>
                        {subscriber.email}
                      </p>
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold whitespace-nowrap"
                        style={{
                          backgroundColor: subscriber.isActive ? '#10B98120' : '#EF444420',
                          color: subscriber.isActive ? '#10B981' : '#EF4444'
                        }}
                      >
                        {subscriber.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleSubscriberStatus(subscriber.email, subscriber.isActive)}
                        className="flex-1 p-1 rounded text-xs font-medium transition-all hover:scale-105"
                        style={{
                          backgroundColor: subscriber.isActive ? '#EF444420' : '#10B98120',
                          color: subscriber.isActive ? '#EF4444' : '#10B981'
                        }}
                      >
                        {subscriber.isActive ? (
                          <ToggleRight className="w-4 h-4 inline mr-1" />
                        ) : (
                          <ToggleLeft className="w-4 h-4 inline mr-1" />
                        )}
                        {subscriber.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteSubscriber(subscriber.email)}
                        className="p-2 rounded text-xs transition-all hover:scale-110"
                        style={{
                          backgroundColor: '#EF444420',
                          color: '#EF4444'
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  )
}

export default NewsletterAdmin